// 画面で共有する状態。通貨・権利・育成・進行は「ここだけ」に持つ（各画面は独自のコピーを作らない）。
// 正本はサーバー。/api/me の応答をそのまま入れ、操作のあとは refreshMe で取り直す。
import { META_CONFIG, nextStage, type CharId, type Profile } from '@hyakki/core';
import { api, ApiError, type MeResponse, type Resources, type StaminaView } from './api';
import { advanceStamina } from './stamina-tick';
import { setShikiStageGetter } from './assets';

export interface Settings {
  /** 全体の音量（BGM・効果音にかかる） */
  master?: number;
  bgm: number; // 0..1
  sfx: number;
  fxSpeed: 1 | 2 | 3; // 演出速度
  reduceShake: boolean; // 揺れ・強い発光の軽減
  /** ほかの人の実績（式神が仲間に・国の制圧）を全体チャットに出さない（この端末だけ） */
  hideOthersFeats?: boolean;
  /** 戦闘の行動の選び方：false＝下のボタン列（前の操作）。未設定は押した所に出るパレット */
  palette?: boolean;
  /** 節電モード中の寸劇（手番ごとに短く動く）。未設定は出す */
  psSkit?: boolean;
  battleSpeed: 1 | 2 | 4;
  /** 速度の既定を×2にした版（2026-11-03）。古い端末の等速を一度だけ×2に上げた印 */
  speedV?: number;
  chatOpen: boolean; // PC 右カラムの開閉
  autoTarget: number | null; // オートの周回先
  autoRetryEvery: number; // 未クリアへ挑戦するか（0＝しない。旧：何周ごとに再挑戦）
  autoOnLose?: 'stop' | 'back' | 'retry'; // 負けたとき
  autoRetryTimes?: number; // 再挑戦：この回数続けて負けたら止める
  autoLimit?: { kind: 'none' } | { kind: 'count'; n: number } | { kind: 'until'; hhmm: string }; // オート周回の終了条件
  autoFast?: boolean; // オート周回で演出を省く（テストサーバー・開発環境だけ）
  autoFarm?: 'selected' | 'last' | number; // 周回先：選択中の戦場／直近の勝利先／指定の戦場（無ければ autoTarget を読む）
  autoShiki?: boolean; // オートの目標：式神候補を必ず倒す
  autoRelays?: boolean; // オートの目標：龍脈をすべて取る（★3）
  autoStory?: boolean; // オート周回中に物語の場面が来たら見る（既定はスキップ＝あとで手動のときに出る）
  skipDefenseIntro?: boolean; // 防衛戦の説明を次回から出さない（1秒の告知だけ）
}

export interface Progress {
  stars: Record<string, [boolean, boolean, boolean]>; // stageId → 星（通常難易度）
  cleared: number[];
  /** 次の目安（出撃できる未クリアの戦場で一番弱いもの。全部クリアなら null）。国盗りは順番に進まないので「最大」ではなくこれを使う */
  next: number | null;
  diffCleared: Record<string, number>; // stageId → クリア済みの最高難易度+1
}

export interface AppState {
  me: MeResponse | null;
  resources: Resources;
  /** 永続権利のキー（exp2x / auto / speed4 / omamori:<id> …） */
  entitlements: Set<string>;
  items: Record<string, number>;
  stamina: StaminaView | null;
  profile: Profile | null;
  progress: Progress | null;
  oshi: CharId;
  settings: Settings;
  /** 端末に保存してある中断中の戦闘（「戦闘に戻る」） */
  suspended: { stageId: number; charId: CharId; savedAt: number } | null;
  online: boolean;
  maintenance: { message: string } | null;
  pendingOrder: string | null; // 購入の反映待ち
  /** テストサーバー・開発環境（/api/status の testMode）。テスト用の機能を出す */
  testMode: boolean;
}

const SETTINGS_KEY = 'hk.settings';
const DEFAULT_SETTINGS: Settings = {
  bgm: 0.6,
  sfx: 0.8,
  fxSpeed: 1,
  reduceShake: false,
  // 使えるところまでに抑える（ゲストは等速、Google 連携は×2、倍速パックで×4。screens/battle.ts の maxSpeed）
  battleSpeed: 2,
  chatOpen: true,
  autoTarget: null,
  autoRetryEvery: 5,
};

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const s = { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
      // 2026-11-03 に Google 連携で×2を無料に：それまで等速しか選べなかった端末は一度だけ×2に上げる
      if (!s.speedV) {
        if (s.battleSpeed === 1) s.battleSpeed = 2;
        s.speedV = 2;
      }
      return s;
    }
  } catch {
    /* 既定値 */
  }
  return { ...DEFAULT_SETTINGS, speedV: 2 };
}

export const state: AppState = {
  me: null,
  resources: {},
  entitlements: new Set(),
  items: {},
  stamina: null,
  profile: null,
  progress: null,
  oshi: 'akari',
  settings: loadSettings(),
  suspended: null,
  online: navigator.onLine,
  maintenance: null,
  pendingOrder: null,
  testMode: false,
};

// 式神の絵（進化の姿）は持っている★で
setShikiStageGetter((id) => Math.max(1, state.profile?.shikigami?.[id]?.star ?? 1));

type Key = keyof AppState;
type Sub = (changed: Set<Key>) => void;
const subs = new Set<Sub>();
let pending = new Set<Key>();
let scheduled = false;

export function subscribe(fn: Sub) {
  subs.add(fn);
  return () => subs.delete(fn);
}

export function patch(p: Partial<AppState>) {
  Object.assign(state, p);
  for (const k of Object.keys(p) as Key[]) pending.add(k);
  if (p.settings) {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
    } catch {
      /* 保存できなくても続行 */
    }
  }
  if (!scheduled) {
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const changed = pending;
      pending = new Set();
      subs.forEach((f) => f(changed));
    });
  }
}

export const setSetting = <K extends keyof Settings>(k: K, v: Settings[K]) => patch({ settings: { ...state.settings, [k]: v } });

const bits = (n: number): [boolean, boolean, boolean] => [!!(n & 1), !!(n & 2), !!(n & 4)];

function progressOf(me: MeResponse): Progress {
  const stars: Progress['stars'] = {};
  const cleared: number[] = [];
  const diffCleared: Record<string, number> = {};
  for (const r of me.stages ?? []) {
    if (r.difficulty === 0) {
      stars[String(r.stageId)] = bits(r.stars);
      if (r.clears > 0) cleared.push(r.stageId);
    }
    if (r.clears > 0) diffCleared[String(r.stageId)] = Math.max(diffCleared[String(r.stageId)] ?? 0, r.difficulty + 1);
  }
  // 応答に stages が無い場合は Profile.progress から
  if (!me.stages && me.profile?.progress) {
    for (const [k, v] of Object.entries(me.profile.progress)) {
      if (k.includes(':')) continue;
      stars[k] = v.stars;
      if (v.clears > 0) cleared.push(Number(k));
    }
  }
  const next = me.profile ? nextStage(me.profile) : (me.worldmap?.nextStage ?? 1);
  return { stars, cleared, next, diffCleared };
}

/** /api/me の応答を状態へ反映する */
export function applyMe(me: MeResponse) {
  patch({
    me,
    resources: me.resources ?? {},
    entitlements: new Set(me.entitlements ?? []),
    items: me.items ?? {},
    stamina: me.stamina ?? null,
    profile: me.profile ?? null,
    oshi: me.profile?.oshi ?? state.oshi,
    progress: progressOf(me),
  });
}

let inflight: Promise<MeResponse> | null = null;
export function refreshMe(): Promise<MeResponse> {
  // 連続して呼ばれても1回にまとめる
  inflight ??= api
    .me()
    .then((me) => {
      if (!me || typeof me !== 'object' || !me.user) throw new ApiError(502, 'bad_response');
      applyMe(me);
      return me;
    })
    .finally(() => (inflight = null));
  return inflight;
}

/** 育成などの操作：成功したら /api/me を取り直す（応答の形に依存しない） */
export async function act<T>(p: Promise<T>): Promise<T> {
  const r = await p;
  await refreshMe().catch(() => {});
  return r;
}

export const starsOf = (stageId: number): [boolean, boolean, boolean] => state.progress?.stars[String(stageId)] ?? [false, false, false];
export const totalStars = () => Object.values(state.progress?.stars ?? {}).reduce((s, x) => s + x.filter(Boolean).length, 0);
export const isCleared = (stageId: number) => !!state.progress?.cleared.includes(stageId);
export const hasEnt = (key: string) => state.entitlements.has(key);
export const levelNow = () => state.me?.level?.level ?? state.profile?.level ?? 1;

/**
 * 兵糧を時間の分だけ進める（ページを開いたままでも表示が増えるように）。サーバーの回復と同じ：nextAt ごとに1、本体が上限なら予備へ。
 * 手元のプロフィールからは計算し直さない（出撃で受け取った最新の値が profile.stamina に入っていないため）。次にサーバーから受け取れば正しい値に揃う
 */
export function tickStamina(now = Date.now()) {
  const st = state.stamina;
  if (!st) return;
  const next = advanceStamina(st, now, META_CONFIG.stamina.regenMinutes * 60_000);
  if (next !== st) patch({ stamina: next });
}

export { ApiError };
